import {computed, reactive, ref} from 'vue'
import Decimal from 'decimal.js'
import type {
  JobDefinition,
  JobStatisticBucket,
  JobStatistics,
  JobStatus,
  ScheduleJobRequest,
} from '@/backend/jobs.types'

export interface JobDefinitionDetailsSummary {
  total: Decimal
  successful: Decimal
  failed: Decimal
  avgExecution: Decimal | null
  minExecution: Decimal | null
  maxExecution: Decimal | null
  successRate: number
}

export interface JobDefinitionDetailsStoreServices {
  jobsService: {
    getStatistics: (params?: { jobDefinitionId?: string; from?: Date; to?: Date }) => Promise<JobStatistics>
    getJobDefinition: (definitionId: string, occurrences?: number) => Promise<JobDefinition>
    updateJobDefinitionSchedule: (definitionId: string, patch: Record<string, unknown>) => Promise<JobDefinition>
    scheduleJob: (request: ScheduleJobRequest) => Promise<string>
    cancelJob: (jobId: string) => Promise<void>
    getJob: (jobId: string) => Promise<JobStatus>
  }
}

export function useJobDefinitionDetailsStore(
  jobDefinitionId: string,
  services: JobDefinitionDetailsStoreServices
) {
  const { jobsService } = services

  const loading = ref(false)
  const error = ref<Error | null>(null)
  const statistics = ref<JobStatisticBucket[]>([])
  const scheduled = ref<Decimal>(new Decimal(0))
  const due = ref<Decimal>(new Decimal(0))
  const selectedJobId = ref<string | null>(null)
  const definition = ref<JobDefinition | null>(null)

  const summary = computed<JobDefinitionDetailsSummary | null>(() => {
    if (statistics.value.length === 0) return null

    let totalSuccessful = new Decimal(0)
    let totalFailed = new Decimal(0)
    let totalAvg = new Decimal(0)
    let avgCount = 0
    let globalMin: Decimal | null = null
    let globalMax: Decimal | null = null

    for (const stat of statistics.value) {
      totalSuccessful = totalSuccessful.add(stat.successful)
      totalFailed = totalFailed.add(stat.failed)

      if (stat.avgExecution !== null && stat.avgExecution !== undefined) {
        totalAvg = totalAvg.add(stat.avgExecution ?? 0)
        avgCount++
      }

      if (stat.minExecution !== null && stat.minExecution !== undefined) {
        if (globalMin === null || stat.minExecution.lt(globalMin)) {
          globalMin = stat.minExecution
        }
      }

      if (stat.maxExecution !== null && stat.maxExecution !== undefined) {
        if (globalMax === null || stat.maxExecution.gt(globalMax)) {
          globalMax = stat.maxExecution
        }
      }
    }

    const total = totalSuccessful.add(totalFailed)
    const successRate = total.gt(0) ? totalSuccessful.div(total).mul(100).toNumber() : 0
    const avgExecution = avgCount > 0 ? totalAvg.div(avgCount) : null

    return {
      total,
      successful: totalSuccessful,
      failed: totalFailed,
      avgExecution,
      minExecution: globalMin,
      maxExecution: globalMax,
      successRate,
    }
  })

  async function refresh() {
    loading.value = true
    error.value = null

    try {
      const now = new Date()
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000) // Last 24 hours

      const result = await jobsService.getStatistics({
        jobDefinitionId,
        from,
        to: now,
      })

      const statDefinition = result.definitions.find((d) => d.jobDefinitionId === jobDefinitionId)
      statistics.value = statDefinition?.buckets ?? []
      scheduled.value = statDefinition?.scheduled ?? new Decimal(0)
      due.value = statDefinition?.due ?? new Decimal(0)
    } catch (e) {
      error.value = e as Error
    } finally {
      loading.value = false
    }

    await loadSchedule()
  }

  async function loadSchedule() {
    try {
      definition.value = await jobsService.getJobDefinition(jobDefinitionId)
    } catch (e) {
      error.value = e as Error
    }
  }

  async function saveSchedule(patch: Record<string, unknown>): Promise<void> {
    definition.value = await jobsService.updateJobDefinitionSchedule(jobDefinitionId, patch)
  }

  async function previewOccurrences(count: number): Promise<void> {
    const result = await jobsService.getJobDefinition(jobDefinitionId, count)
    definition.value = definition.value
      ? { ...definition.value, occurrences: result.occurrences }
      : result
  }

  async function scheduleJob(parameters?: Record<string, unknown>): Promise<string> {
    return await jobsService.scheduleJob({
      jobDefinitionId,
      parameters,
    })
  }

  async function cancelJob(jobId: string): Promise<void> {
    await jobsService.cancelJob(jobId)
  }

  async function getJob(jobId: string): Promise<JobStatus> {
    return await jobsService.getJob(jobId)
  }

  function selectJob(jobId: string | null) {
    selectedJobId.value = jobId
  }

  return reactive({
    jobDefinitionId,
    loading,
    error,
    statistics,
    scheduled,
    due,
    summary,
    selectedJobId,
    definition,
    refresh,
    saveSchedule,
    previewOccurrences,
    scheduleJob,
    cancelJob,
    getJob,
    selectJob,
  })
}
